import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  FormField,
  Header,
  Input,
  Link,
  Modal,
  ProgressBar,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
} from "@amzn/awsui-components-react";
import {
  Stage,
  UatProject,
  UatProjectOrder,
  UatProjectSync,
} from "../../lusca-search-api/generated-src";
import useAsanaAuthorization, {
  isAsanaAuthorizationError,
} from "../../hooks/useAsanaAuthorization";
import useUatProjects from "../../hooks/useUatProjects";
import { RedriveApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import useShadowTestResult from "../../hooks/useShadowTestResult";
import useClassifications from "../../hooks/useClassifications";
import ShadowTestResultModal from "../summary/ShadowTestResultModal";
import { getShadowTestResultStatus } from "../../helpers/shadowTestResultStatus";
import { ComparisonEnvironment } from "../../lusca-search-api/generated-src";

function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const responseBody: unknown = error.response?.data;
    if (
      typeof responseBody === "object" &&
      responseBody !== null &&
      "error" in responseBody &&
      typeof (responseBody as { error?: unknown }).error === "string"
    ) {
      return (responseBody as { error: string }).error;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "The UAT Project request could not be completed.";
}

function isRunning(sync?: UatProjectSync): boolean {
  return sync?.status === "pending" || sync?.status === "running";
}

function statusType(
  project: UatProject,
  sync?: UatProjectSync
): "stopped" | "error" | "loading" | "pending" | "success" {
  if (project.isArchived) {
    return "stopped";
  }
  if (isRunning(sync)) {
    return "loading";
  }
  if (project.lastSync?.status === "completed") {
    return "success";
  }
  if (project.lastSync?.status === "failed") {
    return "error";
  }
  return "pending";
}

function statusText(project: UatProject, sync?: UatProjectSync): string {
  if (project.isArchived) {
    return "Archived";
  }
  if (isRunning(sync)) {
    const progress = sync?.progress;
    if (progress && progress.total > 0) {
      return `Syncing ${progress.completed} of ${progress.total} tasks`;
    }
    return sync?.status === "pending"
      ? "Starting Asana sync"
      : "Syncing from Asana";
  }
  if (project.lastSync?.status === "completed") {
    const { importedCount = 0, skippedCount } = project.lastSync;
    return skippedCount === undefined
      ? `Asana sync completed: ${importedCount} orders imported`
      : `Asana sync completed: ${importedCount} orders imported; ${skippedCount} tasks skipped`;
  }
  if (project.lastSync?.status === "failed") {
    return "Asana sync failed";
  }
  return "Awaiting Asana sync";
}

function syncActionText(project: UatProject, sync?: UatProjectSync): string {
  if (isRunning(sync)) {
    return "Syncing from Asana";
  }
  if (project.lastSync?.status === "failed") {
    return "Retry Asana sync";
  }
  if (project.lastSync?.status === "completed") {
    return "Sync again";
  }
  return "Sync from Asana";
}

function healthIndicator(order: UatProjectOrder): React.ReactNode {
  if (order.healthStatus === "healthy") {
    return <StatusIndicator type="success">Healthy</StatusIndicator>;
  }
  if (order.healthStatus === "needs_review") {
    return <StatusIndicator type="warning">Needs review</StatusIndicator>;
  }
  return <StatusIndicator type="pending">Not tested</StatusIndicator>;
}

function asanaStatus(): React.ReactNode {
  return <StatusIndicator type="success">Open</StatusIndicator>;
}

interface ShadowTestStatusCellProps {
  project: UatProject;
  order: UatProjectOrder;
}

function ShadowTestStatusCell({ project, order }: ShadowTestStatusCellProps) {
  const { gammaResult, gammaIsLoading, gammaError } = useShadowTestResult({
    stage: project.stage,
    transactionId: order.transactionId,
  });
  if (gammaIsLoading) {
    return <StatusIndicator type="loading">Checking</StatusIndicator>;
  }
  if (gammaError) {
    return <StatusIndicator type="error">Unavailable</StatusIndicator>;
  }
  if (!gammaResult) {
    return <StatusIndicator type="pending">Not run</StatusIndicator>;
  }

  const status = getShadowTestResultStatus(gammaResult);
  return (
    <StatusIndicator
      type={
        status === "PASSED"
          ? "success"
          : status === "FAILED"
          ? "error"
          : "warning"
      }
    >
      {status === "PASSED"
        ? "Passed"
        : status === "FAILED"
        ? "Failed"
        : "Blocked"}
    </StatusIndicator>
  );
}

interface ProjectOrdersTableProps {
  project: UatProject;
  orders: UatProjectOrder[];
}

function ProjectOrdersTable({ project, orders }: ProjectOrdersTableProps) {
  const [redrivingOrderId, setRedrivingOrderId] = useState<string>();
  const [redriveMessage, setRedriveMessage] = useState<string>();
  const [redriveError, setRedriveError] = useState<string>();
  const [selectedShadowOrder, setSelectedShadowOrder] =
    useState<UatProjectOrder>();
  const [comparisonEnvironment, setComparisonEnvironment] =
    useState<ComparisonEnvironment>(ComparisonEnvironment.Gamma);
  const classifications = useClassifications(project.stage);
  const {
    gammaResult,
    betaResult,
    gammaIsLoading,
    betaIsLoading,
    gammaIsFetching,
    betaIsFetching,
    gammaError,
    betaError,
    refetch,
  } = useShadowTestResult({
    stage: project.stage,
    transactionId: selectedShadowOrder?.transactionId,
  });
  const shadowTestResult =
    comparisonEnvironment === ComparisonEnvironment.Beta
      ? betaResult
      : gammaResult;
  const isShadowTestLoading =
    comparisonEnvironment === ComparisonEnvironment.Beta
      ? betaIsLoading
      : gammaIsLoading;
  const isShadowTestFetching =
    comparisonEnvironment === ComparisonEnvironment.Beta
      ? betaIsFetching
      : gammaIsFetching;
  const shadowTestError =
    comparisonEnvironment === ComparisonEnvironment.Beta
      ? betaError
      : gammaError;
  const availableComparisonEnvironments = [
    ...(gammaResult ? [ComparisonEnvironment.Gamma] : []),
    ...(betaResult ? [ComparisonEnvironment.Beta] : []),
  ];

  const redrive = async (order: UatProjectOrder) => {
    setRedriveMessage(undefined);
    setRedriveError(undefined);
    setRedrivingOrderId(order.transactionId);
    try {
      const response = await RedriveApiFactory().postRedriveOrder(
        project.stage,
        {
          orderId: order.transactionId,
        }
      );
      setRedriveMessage(
        `Redrive requested for ${order.transactionId}${
          response.data.executionId ? ` (${response.data.executionId})` : ""
        }.`
      );
    } catch (error) {
      setRedriveError(errorMessage(error));
    } finally {
      setRedrivingOrderId(undefined);
    }
  };

  return (
    <SpaceBetween size="xs">
      {redriveMessage && <Alert type="success">{redriveMessage}</Alert>}
      {redriveError && <Alert type="error">{redriveError}</Alert>}
      <Table
        items={orders}
        variant="embedded"
        columnDefinitions={[
          {
            id: "order",
            header: "Order ID",
            cell: (order) => (
              <Link
                href={`/find?transactionId=${encodeURIComponent(
                  order.transactionId
                )}`}
              >
                {order.transactionId}
              </Link>
            ),
          },
          {
            id: "test-case",
            header: "Test case",
            cell: (order) => (
              <Link href={order.taskUrl} external>
                {order.taskName}
              </Link>
            ),
          },
          {
            id: "shadow-test",
            header: "Shadow test",
            cell: (order) => (
              <ShadowTestStatusCell project={project} order={order} />
            ),
          },
          {
            id: "validation",
            header: "Validation",
            cell: healthIndicator,
          },
          {
            id: "asana-status",
            header: "Asana",
            cell: asanaStatus,
          },
          {
            id: "actions",
            header: "Actions",
            cell: (order) => (
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  loading={redrivingOrderId === order.transactionId}
                  disabled={Boolean(redrivingOrderId)}
                  onClick={() => redrive(order)}
                >
                  Redrive
                </Button>
                <Button
                  onClick={() => {
                    setComparisonEnvironment(ComparisonEnvironment.Gamma);
                    setSelectedShadowOrder(order);
                  }}
                >
                  Test results
                </Button>
              </SpaceBetween>
            ),
          },
        ]}
        empty={<Box textAlign="center">No imported orders yet.</Box>}
        header={
          <Header variant="h3" counter={`(${orders.length})`}>
            Imported orders
          </Header>
        }
      />
      <ShadowTestResultModal
        visible={Boolean(selectedShadowOrder)}
        onDismiss={() => setSelectedShadowOrder(undefined)}
        result={shadowTestResult}
        isLoading={isShadowTestLoading}
        isRefreshing={isShadowTestFetching}
        error={shadowTestError}
        onRefresh={() => {
          refetch().catch((refreshError) =>
            console.error("Failed to refresh shadow test result:", refreshError)
          );
        }}
        classifications={classifications}
        comparisonEnvironment={comparisonEnvironment}
        availableComparisonEnvironments={availableComparisonEnvironments}
        onComparisonEnvironmentChange={setComparisonEnvironment}
        onTriggerTest={() => {}}
        isTriggerLoading={false}
        triggerError={null}
        canTrigger={false}
        hideTrigger
      />
    </SpaceBetween>
  );
}

interface ActiveSync {
  projectId: string;
  sync: UatProjectSync;
}

interface ProjectGroupProps {
  title: string;
  description: string;
  projects: UatProject[];
  projectOrders: Record<string, UatProjectOrder[]>;
  activeSync?: ActiveSync;
  isUpdating: boolean;
  onArchive: (project: UatProject, archived: boolean) => void;
  onSync: (project: UatProject) => void;
}

function ProjectGroup({
  title,
  description,
  projects,
  projectOrders,
  activeSync,
  isUpdating,
  onArchive,
  onSync,
}: ProjectGroupProps) {
  return (
    <Container
      header={
        <Header variant="h2" counter={`(${projects.length})`}>
          {title}
        </Header>
      }
    >
      <SpaceBetween size="m">
        <Box color="text-body-secondary">{description}</Box>
        {projects.length === 0 ? (
          <Box textAlign="center" color="text-status-inactive" padding="l">
            No {title.toLowerCase()} UAT Projects.
          </Box>
        ) : (
          projects.map((project) => {
            const sync =
              activeSync?.projectId === project.id
                ? activeSync.sync
                : project.activeSync;
            return (
              <Container
                key={project.id}
                header={
                  <Header
                    variant="h3"
                    description={`${project.stage} · Updated ${new Date(
                      project.updatedAt
                    ).toLocaleString()}`}
                    actions={
                      <SpaceBetween direction="horizontal" size="xs">
                        {!project.isArchived && (
                          <Button
                            loading={isRunning(sync)}
                            disabled={isUpdating}
                            onClick={() => onSync(project)}
                          >
                            {syncActionText(project, sync)}
                          </Button>
                        )}
                        <Button
                          loading={isUpdating}
                          onClick={() =>
                            onArchive(project, !project.isArchived)
                          }
                        >
                          {project.isArchived ? "Restore" : "Archive"}
                        </Button>
                      </SpaceBetween>
                    }
                  >
                    {project.projectName}
                  </Header>
                }
              >
                <SpaceBetween size="s">
                  {project.description && <Box>{project.description}</Box>}
                  <SpaceBetween direction="horizontal" size="s">
                    <StatusIndicator type={statusType(project, sync)}>
                      {statusText(project, sync)}
                    </StatusIndicator>
                    <Link href={project.asanaProjectUrl} external>
                      Asana project
                    </Link>
                    <Box color="text-body-secondary">
                      {project.orderCount ?? 0} imported orders
                    </Box>
                  </SpaceBetween>
                  {isRunning(sync) && sync?.progress && (
                    <ProgressBar
                      value={
                        sync.progress.total === 0
                          ? 0
                          : Math.round(
                              (sync.progress.completed / sync.progress.total) *
                                100
                            )
                      }
                      label="Syncing Asana tasks"
                      description={`${sync.progress.completed} of ${sync.progress.total} tasks validated`}
                    />
                  )}
                  {project.lastSync?.error && (
                    <Alert type="warning">{project.lastSync.error}</Alert>
                  )}
                  {project.orderCount !== undefined &&
                    project.orderCount > 0 && (
                      <ProjectOrdersTable
                        project={project}
                        orders={projectOrders[project.id] ?? []}
                      />
                    )}
                  <Box color="text-body-secondary" fontSize="body-s">
                    Sync uses the existing Chat UAT connection and imports its
                    validated order IDs with their Asana task links.
                  </Box>
                </SpaceBetween>
              </Container>
            );
          })
        )}
      </SpaceBetween>
    </Container>
  );
}

export default function ProjectList() {
  const {
    projects,
    projectOrders,
    isLoading,
    error,
    createProject,
    updateProject,
    startSync,
    getSync,
    refresh,
    isCreating,
    isUpdating,
    createError,
    updateError,
    syncError,
  } = useUatProjects();
  const {
    isAuthorized,
    needsReconnect,
    isConnecting,
    authorizationError,
    beginAuthorization,
    markUnauthorized,
  } = useAsanaAuthorization();
  const [createVisible, setCreateVisible] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [description, setDescription] = useState("");
  const [stage, setStage] = useState<Stage>(Stage.Gamma);
  const [asanaProjectUrl, setAsanaProjectUrl] = useState("");
  const [actionError, setActionError] = useState<string>();
  const [activeSync, setActiveSync] = useState<ActiveSync>();
  const [resolvedSyncIds, setResolvedSyncIds] = useState<Set<string>>(
    () => new Set()
  );

  const activeProjects = useMemo(
    () => projects.filter((project) => !project.isArchived),
    [projects]
  );
  const archivedProjects = useMemo(
    () => projects.filter((project) => project.isArchived),
    [projects]
  );

  useEffect(() => {
    if (activeSync) {
      return;
    }
    const resumableProject = projects.find(
      (project) =>
        project.activeSync &&
        isRunning(project.activeSync) &&
        !resolvedSyncIds.has(project.activeSync.syncId)
    );
    if (resumableProject?.activeSync) {
      setActiveSync({
        projectId: resumableProject.id,
        sync: resumableProject.activeSync,
      });
    }
  }, [activeSync, projects, resolvedSyncIds]);

  useEffect(() => {
    if (!activeSync || !isRunning(activeSync.sync)) {
      return undefined;
    }
    const { projectId, sync } = activeSync;
    const timer = window.setTimeout(() => {
      getSync(projectId, sync.syncId)
        .then((nextSync) => {
          if (isRunning(nextSync)) {
            setActiveSync({ projectId, sync: nextSync });
            return;
          }
          setResolvedSyncIds((current) =>
            new Set(current).add(nextSync.syncId)
          );
          setActiveSync(undefined);
          refresh().catch((refreshFailure: unknown) =>
            setActionError(errorMessage(refreshFailure))
          );
        })
        .catch((pollFailure: unknown) => {
          if (isAsanaAuthorizationError(pollFailure)) {
            markUnauthorized();
          }
          setActionError(errorMessage(pollFailure));
          setActiveSync(undefined);
        });
    }, sync.retryAfterMs ?? 1000);
    return () => window.clearTimeout(timer);
  }, [activeSync, getSync, markUnauthorized, refresh]);

  const closeCreateModal = () => {
    if (!isCreating) {
      setCreateVisible(false);
      setActionError(undefined);
    }
  };

  const create = () => {
    setActionError(undefined);
    createProject({
      projectName,
      description: description || undefined,
      stage,
      asanaProjectUrl,
    })
      .then(() => {
        setProjectName("");
        setDescription("");
        setStage(Stage.Gamma);
        setAsanaProjectUrl("");
        setCreateVisible(false);
      })
      .catch((createFailure: unknown) =>
        setActionError(errorMessage(createFailure))
      );
  };

  const archive = (project: UatProject, isArchived: boolean) => {
    setActionError(undefined);
    updateProject(project.id, { version: project.version, isArchived }).catch(
      (updateFailure: unknown) => setActionError(errorMessage(updateFailure))
    );
  };

  const connectAsana = () => {
    setActionError(undefined);
    beginAuthorization(needsReconnect).catch((authorizationFailure: unknown) =>
      setActionError(errorMessage(authorizationFailure))
    );
  };

  const sync = (project: UatProject) => {
    setActionError(undefined);
    startSync(project.id)
      .then((syncResult) => {
        if (isRunning(syncResult)) {
          setActiveSync({ projectId: project.id, sync: syncResult });
        } else {
          setResolvedSyncIds((current) =>
            new Set(current).add(syncResult.syncId)
          );
          refresh().catch((refreshFailure: unknown) =>
            setActionError(errorMessage(refreshFailure))
          );
        }
      })
      .catch((syncFailure: unknown) => {
        if (isAsanaAuthorizationError(syncFailure)) {
          markUnauthorized();
        }
        setActionError(errorMessage(syncFailure));
      });
  };

  let projectContent: React.ReactNode;
  if (error) {
    projectContent = <Alert type="error">{errorMessage(error)}</Alert>;
  } else if (isLoading) {
    projectContent = (
      <Box textAlign="center" padding="l">
        <StatusIndicator type="loading">Loading UAT Projects</StatusIndicator>
      </Box>
    );
  } else {
    projectContent = (
      <SpaceBetween size="l">
        <ProjectGroup
          title="Active Projects"
          description="Projects ready for an Asana sync."
          projects={activeProjects}
          projectOrders={projectOrders}
          activeSync={activeSync}
          isUpdating={isUpdating}
          onArchive={archive}
          onSync={sync}
        />
        <ProjectGroup
          title="Archived Projects"
          description="Retained project definitions kept for audit and later restoration."
          projects={archivedProjects}
          projectOrders={projectOrders}
          activeSync={activeSync}
          isUpdating={isUpdating}
          onArchive={archive}
          onSync={sync}
        />
      </SpaceBetween>
    );
  }

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h1"
            description="Shared UAT Projects are durable team collections sourced from Asana."
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                {!isAuthorized && (
                  <Button loading={isConnecting} onClick={connectAsana}>
                    {needsReconnect ? "Reconnect Asana" : "Connect Asana"}
                  </Button>
                )}
                <Button
                  variant="primary"
                  onClick={() => setCreateVisible(true)}
                >
                  Create UAT Project
                </Button>
              </SpaceBetween>
            }
          >
            Raven UAT Projects
          </Header>
        }
      >
        <Alert type="info">
          Sync from Asana uses the existing Chat UAT connection. Refreshing this
          page does not restart a sync; it resumes the stored job status.
        </Alert>
      </Container>
      {actionError && <Alert type="error">{actionError}</Alert>}
      {authorizationError && <Alert type="error">{authorizationError}</Alert>}
      {createError && <Alert type="error">{errorMessage(createError)}</Alert>}
      {updateError && <Alert type="error">{errorMessage(updateError)}</Alert>}
      {syncError && <Alert type="error">{errorMessage(syncError)}</Alert>}
      {projectContent}
      <Modal
        visible={createVisible}
        onDismiss={closeCreateModal}
        header="Create shared UAT Project"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={closeCreateModal} disabled={isCreating}>
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={isCreating}
                disabled={!projectName.trim() || !asanaProjectUrl.trim()}
                onClick={create}
              >
                Create project
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FormField label="Project name">
            <Input
              value={projectName}
              onChange={({ detail }) => setProjectName(detail.value)}
              placeholder="TVOD EU5 expansion"
            />
          </FormField>
          <FormField label="Description">
            <Input
              value={description}
              onChange={({ detail }) => setDescription(detail.value)}
              placeholder="What this UAT validates"
            />
          </FormField>
          <FormField label="Target stage">
            <Select
              selectedOption={{ label: stage, value: stage }}
              options={Object.values(Stage).map((value) => ({
                label: value,
                value,
              }))}
              onChange={({ detail }) =>
                setStage(detail.selectedOption.value as Stage)
              }
            />
          </FormField>
          <FormField
            label="Asana project URL"
            description="Paste the project URL. Lusca Search derives and stores its numeric Asana ID."
          >
            <Input
              value={asanaProjectUrl}
              onChange={({ detail }) => setAsanaProjectUrl(detail.value)}
              placeholder="https://app.asana.com/0/1200123456789012/list"
            />
          </FormField>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}
